import React from 'react';

/**
 * One visual per workflow stage. Each uses the form that actually represents
 * the work — preparation looks like station lanes, a ledger looks like a ledger.
 * Only Analytics gets a chart, because only Analytics is about a trend.
 */

interface VisualProps {
  active: boolean;
}

/** Staggered entrance shared by every row-based visual. */
const rowStyle = (active: boolean, i: number): React.CSSProperties => ({
  transitionDelay: `${i * 70}ms`,
  opacity: active ? 1 : 0.35,
  transform: active ? 'translateY(0)' : 'translateY(6px)',
});

const Shell: React.FC<{ label: string; title: string; children: React.ReactNode }> = ({
  label,
  title,
  children,
}) => (
  <>
    <div className="flex items-center justify-between mb-5">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</p>
        <p className="font-display text-lg font-semibold text-white">{title}</p>
      </div>
      <span className="flex items-center gap-1.5 text-[10px] text-primary-400">
        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
        Live
      </span>
    </div>
    {children}
  </>
);

/* ------------------------------------------------------------------ */
/* Orders — incoming tickets across channels                           */
/* ------------------------------------------------------------------ */

const ORDERS = [
  { channel: 'In-store', ref: '#1054', items: '3 items', total: '₦18,400', tone: 'primary' },
  { channel: 'Counter', ref: 'Walk-in', items: '2 items', total: '₦4,500', tone: 'muted' },
  { channel: 'Delivery', ref: 'Partner', items: '5 items', total: '₦12,000', tone: 'muted' },
  { channel: 'Online', ref: '#1057', items: '1 item', total: '₦26,000', tone: 'muted' },
];

export const OrdersVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Workflow" title="Orders">
    <div className="space-y-2">
      {ORDERS.map((o, i) => (
        <div
          key={o.ref}
          className="flex items-center gap-3 p-3 rounded-lg bg-surface-card border border-surface-border transition-all duration-500"
          style={rowStyle(active, i)}
        >
          <span
            className={`px-2 py-0.5 rounded text-[10px] font-medium shrink-0 ${
              o.tone === 'primary'
                ? 'bg-primary-500/15 text-primary-400 border border-primary-500/30'
                : 'bg-surface-hover text-muted border border-surface-border'
            }`}
          >
            {o.channel}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white/90 truncate">{o.items}</p>
            <p className="text-[11px] text-muted">{o.ref}</p>
          </div>
          <span className="text-sm text-white font-medium shrink-0">{o.total}</span>
        </div>
      ))}
    </div>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Preparation — station lanes with elapsed timers                     */
/* ------------------------------------------------------------------ */

const STATIONS = [
  { name: 'Prep', tickets: [{ t: '#1054 · 3 items', m: '04:12', late: false }, { t: '#1047 · 9 items', m: '11:38', late: true }] },
  { name: 'Pack', tickets: [{ t: '#1051 · 2 items', m: '02:05', late: false }] },
  { name: 'Dispatch', tickets: [{ t: '#1044 · 1 item', m: '01:22', late: false }, { t: '#1052 · 4 items', m: '00:47', late: false }] },
];

export const PreparationVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Workflow" title="Preparation">
    <div className="grid grid-cols-3 gap-2">
      {STATIONS.map((station, si) => (
        <div
          key={station.name}
          className="rounded-lg border border-surface-border bg-surface-card p-2.5 transition-all duration-500"
          style={rowStyle(active, si)}
        >
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[11px] font-medium text-white">{station.name}</p>
            <span className="text-[10px] text-muted">{station.tickets.length}</span>
          </div>
          <div className="space-y-1.5">
            {station.tickets.map((ticket) => (
              <div
                key={ticket.t}
                className={`rounded-md px-2 py-2 border ${
                  ticket.late
                    ? 'border-amber-500/40 bg-amber-500/10'
                    : 'border-surface-border bg-surface'
                }`}
              >
                <p className="text-[10px] text-white/85 leading-tight mb-1.5 truncate">{ticket.t}</p>
                <p
                  className={`text-[11px] font-mono ${
                    ticket.late ? 'text-amber-400' : 'text-primary-400'
                  }`}
                >
                  {ticket.m}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    <p className="mt-3 text-[11px] text-muted">
      Longest ticket <span className="text-amber-400">11:38</span> — Prep running behind
    </p>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Inventory — stock levels depleting, not a bar chart                 */
/* ------------------------------------------------------------------ */

const STOCK = [
  { item: 'Gift Box — Large', level: 72, qty: '36 units', low: false },
  { item: 'Malbec 2019', level: 28, qty: '14 btl', low: true },
  { item: 'Signature Blend', level: 61, qty: '22 kg', low: false },
  { item: 'Packaging Sleeves', level: 12, qty: '3 packs', low: true },
];

export const InventoryVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Workflow" title="Inventory">
    <div className="space-y-3">
      {STOCK.map((s, i) => (
        <div key={s.item} className="transition-all duration-500" style={rowStyle(active, i)}>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-sm text-white/90">{s.item}</span>
            <span className={`text-xs font-mono ${s.low ? 'text-amber-400' : 'text-muted'}`}>
              {s.qty}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-border overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-out ${
                s.low ? 'bg-amber-500' : 'bg-primary-500'
              }`}
              style={{ width: active ? `${s.level}%` : '4%' }}
            />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-center gap-2 text-[11px] text-amber-400 border border-amber-500/25 bg-amber-500/5 rounded-lg px-3 py-2">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
      2 items below reorder point — purchase order drafted
    </div>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Storefront — commerce catalogue and cart                            */
/* ------------------------------------------------------------------ */

const PRODUCTS = [
  { name: 'Malbec 2019', price: '₦26,000', tag: 'Wine' },
  { name: 'Gift Set', price: '₦48,000', tag: 'Celebration' },
  { name: 'Champagne', price: '₦72,000', tag: 'Wine' },
  { name: 'Tasting Box', price: '₦35,000', tag: 'Curated' },
];

export const StorefrontVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Commerce" title="Storefront">
    <div className="grid grid-cols-2 gap-2">
      {PRODUCTS.map((p, i) => (
        <div
          key={p.name}
          className="rounded-lg border border-surface-border bg-surface-card overflow-hidden transition-all duration-500"
          style={rowStyle(active, i)}
        >
          {/* Product image placeholder — a bottle silhouette reads as retail */}
          <div className="h-14 bg-gradient-to-br from-primary-500/15 to-surface-hover flex items-center justify-center">
            <svg className="w-5 h-8 text-primary-500/70" viewBox="0 0 24 40" fill="currentColor">
              <path d="M10 2h4v9c0 1 3 3.5 3 7v18a2 2 0 01-2 2H9a2 2 0 01-2-2V18c0-3.5 3-6 3-7V2z" opacity="0.85" />
            </svg>
          </div>
          <div className="p-2.5">
            <p className="text-[10px] text-primary-400 mb-0.5">{p.tag}</p>
            <p className="text-[11px] text-white/90 truncate">{p.name}</p>
            <p className="text-xs text-white font-medium mt-1">{p.price}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-center justify-between rounded-lg border border-primary-500/30 bg-primary-500/5 px-3 py-2.5">
      <span className="text-[11px] text-muted">Cart · 3 items</span>
      <span className="text-sm text-white font-medium">₦109,000</span>
    </div>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Fulfilment — order journey as a timeline                            */
/* ------------------------------------------------------------------ */

const STEPS = [
  { label: 'Order placed', time: '12:04', done: true },
  { label: 'Payment confirmed', time: '12:05', done: true },
  { label: 'Packed', time: '12:19', done: true },
  { label: 'Out for delivery', time: '12:31', done: false, current: true },
  { label: 'Delivered', time: 'ETA 13:05', done: false },
];

export const FulfilmentVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Commerce" title="Fulfilment">
    <div className="relative pl-1">
      {STEPS.map((step, i) => (
        <div
          key={step.label}
          className="relative flex items-start gap-3 pb-4 last:pb-0 transition-all duration-500"
          style={rowStyle(active, i)}
        >
          {/* Connector */}
          {i < STEPS.length - 1 && (
            <span
              className={`absolute left-[7px] top-4 bottom-0 w-px ${
                step.done ? 'bg-primary-500/50' : 'bg-surface-border'
              }`}
            />
          )}
          <span
            className={`relative z-10 mt-1 w-3.5 h-3.5 rounded-full shrink-0 border-2 ${
              step.done
                ? 'bg-primary-500 border-primary-500'
                : step.current
                  ? 'bg-surface border-primary-500 animate-pulse'
                  : 'bg-surface border-surface-border'
            }`}
          />
          <div className="flex-1 flex items-baseline justify-between gap-3 min-w-0">
            <span className={`text-sm truncate ${step.done || step.current ? 'text-white/90' : 'text-muted'}`}>
              {step.label}
            </span>
            <span className={`text-[11px] font-mono shrink-0 ${step.current ? 'text-primary-400' : 'text-muted'}`}>
              {step.time}
            </span>
          </div>
        </div>
      ))}
    </div>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Accounting — a ledger that balances                                 */
/* ------------------------------------------------------------------ */

const LEDGER = [
  { account: 'Food sales', debit: '', credit: '₦842,000' },
  { account: 'Beverage sales', debit: '', credit: '₦318,500' },
  { account: 'Cost of goods', debit: '₦406,200', credit: '' },
  { account: 'Staff wages', debit: '₦214,000', credit: '' },
];

export const AccountingVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Workflow" title="Accounting">
    <div className="rounded-lg border border-surface-border overflow-hidden">
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 bg-surface-hover text-[10px] uppercase tracking-wider text-muted">
        <span>Account</span>
        <span className="text-right w-20">Debit</span>
        <span className="text-right w-20">Credit</span>
      </div>
      {LEDGER.map((row, i) => (
        <div
          key={row.account}
          className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2.5 border-t border-surface-border text-sm transition-all duration-500"
          style={rowStyle(active, i)}
        >
          <span className="text-white/85 truncate">{row.account}</span>
          <span className="text-right w-20 font-mono text-xs text-muted">{row.debit || '—'}</span>
          <span className="text-right w-20 font-mono text-xs text-primary-400">{row.credit || '—'}</span>
        </div>
      ))}
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2.5 border-t border-primary-500/30 bg-primary-500/5 text-sm">
        <span className="text-white font-medium">Gross margin</span>
        <span className="text-right w-20" />
        <span className="text-right w-20 font-mono text-xs text-white font-medium">₦540,300</span>
      </div>
    </div>
    <p className="mt-3 text-[11px] text-muted">Reconciled automatically · no spreadsheet export</p>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Analytics — the one place a chart belongs                           */
/* ------------------------------------------------------------------ */

const SERIES = [28, 42, 36, 58, 47, 71, 64, 88];

export const AnalyticsVisual: React.FC<VisualProps> = ({ active }) => {
  const w = 300;
  const h = 110;
  const max = Math.max(...SERIES);
  const points = SERIES.map((v, i) => {
    const x = (i / (SERIES.length - 1)) * w;
    const y = h - (v / max) * (h - 12) - 6;
    return { x, y };
  });

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;

  return (
    <Shell label="Workflow" title="Analytics">
      <div className="rounded-lg border border-surface-border bg-surface-card p-3">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted">Revenue · 8 weeks</p>
            <p className="font-display text-xl font-semibold text-white">₦4.28M</p>
          </div>
          <span className="text-[11px] px-2 py-1 rounded bg-primary-500/15 text-primary-400 border border-primary-500/30">
            ▲ 31.4%
          </span>
        </div>

        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none" role="img" aria-label="Revenue trend, up 31 percent over eight weeks">
          {[0.25, 0.5, 0.75].map((g) => (
            <line key={g} x1="0" y1={h * g} x2={w} y2={h * g} stroke="currentColor" className="text-surface-border" strokeWidth="1" />
          ))}
          <defs>
            <linearGradient id="analyticsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16A34A" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#16A34A" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={area}
            fill="url(#analyticsFill)"
            style={{ opacity: active ? 1 : 0, transition: 'opacity 700ms ease-out' }}
          />
          <path
            d={line}
            fill="none"
            stroke="#16A34A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            style={{
              strokeDasharray: 1,
              strokeDashoffset: active ? 0 : 1,
              transition: 'stroke-dashoffset 1100ms ease-out',
            }}
          />
          <circle
            cx={points[points.length - 1].x - 2}
            cy={points[points.length - 1].y}
            r="3.5"
            fill="#16A34A"
            style={{ opacity: active ? 1 : 0, transition: 'opacity 400ms ease-out 800ms' }}
          />
        </svg>

        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-surface-border">
          {[
            { k: 'Avg ticket', v: '₦8,240' },
            { k: 'Covers', v: '1,284' },
            { k: 'Best branch', v: 'Wuse II' },
          ].map((m) => (
            <div key={m.k}>
              <p className="text-[10px] text-muted">{m.k}</p>
              <p className="text-xs text-white font-medium mt-0.5">{m.v}</p>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

/* ------------------------------------------------------------------ */
/* AI — recommendations with confidence                                */
/* ------------------------------------------------------------------ */

const INSIGHTS = [
  { text: 'Reduce Friday prep volume by 18% — recurring waste detected', confidence: 92 },
  { text: 'Reorder Malbec 2019 before Thursday — projected stock-out', confidence: 87 },
  { text: 'Move 2 staff to Bar 6–8pm — queue times rising', confidence: 74 },
];

const ConfidenceRing: React.FC<{ value: number; active: boolean }> = ({ value, active }) => {
  const r = 12;
  const circumference = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8 shrink-0 -rotate-90" aria-hidden>
      <circle cx="16" cy="16" r={r} fill="none" stroke="currentColor" className="text-surface-border" strokeWidth="3" />
      <circle
        cx="16"
        cy="16"
        r={r}
        fill="none"
        stroke="#16A34A"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={active ? circumference * (1 - value / 100) : circumference}
        style={{ transition: 'stroke-dashoffset 900ms ease-out' }}
      />
    </svg>
  );
};

export const AiVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Intelligence" title="AI Insights">
    <div className="space-y-2">
      {INSIGHTS.map((insight, i) => (
        <div
          key={insight.text}
          className="flex items-center gap-3 p-3 rounded-lg border border-primary-500/20 bg-primary-500/5 transition-all duration-500"
          style={rowStyle(active, i)}
        >
          <div className="relative shrink-0">
            <ConfidenceRing value={insight.confidence} active={active} />
            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-medium text-white">
              {insight.confidence}
            </span>
          </div>
          <p className="text-[12px] leading-snug text-white/85">{insight.text}</p>
        </div>
      ))}
    </div>
    <p className="mt-3 text-[11px] text-muted">
      Recommendations refresh as operations and sales data change.
    </p>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Customers — the record behind a name, not a chart                   */
/* ------------------------------------------------------------------ */

const CUSTOMERS = [
  { name: 'Ada Obi', segment: 'Regular', visits: 24, spend: '₦486,000', tier: 'Gold' },
  { name: 'Tunde Bello', segment: 'Corporate', visits: 11, spend: '₦1,240,000', tier: 'Platinum' },
  { name: 'Ngozi Ade', segment: 'New', visits: 2, spend: '₦38,500', tier: '—' },
];

export const CustomersVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Relationships" title="Customers">
    <div className="space-y-2">
      {CUSTOMERS.map((c, i) => (
        <div
          key={c.name}
          className="flex items-center gap-3 p-3 rounded-lg bg-surface-card border border-surface-border transition-all duration-500"
          style={rowStyle(active, i)}
        >
          <span className="w-8 h-8 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-400 text-[11px] font-medium flex items-center justify-center shrink-0">
            {c.name.split(' ').map((n) => n[0]).join('')}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white/90 truncate">{c.name}</p>
            <p className="text-[11px] text-muted">
              {c.segment} · {c.visits} orders
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm text-white font-medium">{c.spend}</p>
            <p className="text-[10px] text-muted">{c.tier}</p>
          </div>
        </div>
      ))}
    </div>
    <p className="mt-3 text-[11px] text-muted">
      Lifetime value and order history follow the customer across every channel.
    </p>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Staff — a live shift board with roles and hours                     */
/* ------------------------------------------------------------------ */

const STAFF = [
  { name: 'Chidi O.', role: 'Supervisor', shift: '08:00 — 16:00', on: true, hours: '38h' },
  { name: 'Amaka N.', role: 'Sales', shift: '10:00 — 18:00', on: true, hours: '32h' },
  { name: 'Femi A.', role: 'Fulfilment', shift: '12:00 — 20:00', on: false, hours: '27h' },
];

export const StaffVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="People" title="Staff">
    <div className="space-y-2">
      {STAFF.map((person, i) => (
        <div
          key={person.name}
          className="flex items-center gap-3 p-3 rounded-lg bg-surface-card border border-surface-border transition-all duration-500"
          style={rowStyle(active, i)}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              person.on ? 'bg-primary-500' : 'bg-surface-border'
            }`}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white/90 truncate">{person.name}</p>
            <p className="text-[11px] text-muted">{person.role}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[11px] font-mono text-white/75">{person.shift}</p>
            <p className={`text-[10px] ${person.on ? 'text-primary-400' : 'text-muted'}`}>
              {person.on ? `Clocked in · ${person.hours}` : `Off · ${person.hours}`}
            </p>
          </div>
        </div>
      ))}
    </div>
    <p className="mt-3 text-[11px] text-muted">
      Roles decide what each person can see and approve.
    </p>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* Branches — one standard, compared side by side                      */
/* ------------------------------------------------------------------ */

const BRANCHES = [
  { name: 'Wuse II', revenue: '₦2.4M', share: 82, delta: '+12%', up: true },
  { name: 'Maitama', revenue: '₦1.8M', share: 61, delta: '+4%', up: true },
  { name: 'Garki', revenue: '₦940K', share: 32, delta: '−7%', up: false },
];

export const BranchesVisual: React.FC<VisualProps> = ({ active }) => (
  <Shell label="Network" title="Branches">
    <div className="space-y-2.5">
      {BRANCHES.map((b, i) => (
        <div
          key={b.name}
          className="p-3 rounded-lg bg-surface-card border border-surface-border transition-all duration-500"
          style={rowStyle(active, i)}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-white/90">{b.name}</p>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-white font-medium">{b.revenue}</span>
              <span className={`text-[10px] ${b.up ? 'text-primary-400' : 'text-amber-400'}`}>
                {b.delta}
              </span>
            </div>
          </div>
          <div className="h-1 rounded-full bg-surface-hover overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${
                b.up ? 'bg-primary-500' : 'bg-amber-500/70'
              }`}
              style={{ width: active ? `${b.share}%` : '0%', transitionDelay: `${i * 90}ms` }}
            />
          </div>
        </div>
      ))}
    </div>
    <p className="mt-3 text-[11px] text-muted">
      Same catalogue, pricing, and controls — rolled out to every location at once.
    </p>
  </Shell>
);

/* ------------------------------------------------------------------ */

export const VISUALS: Record<string, React.FC<VisualProps>> = {
  orders: OrdersVisual,
  preparation: PreparationVisual,
  inventory: InventoryVisual,
  storefront: StorefrontVisual,
  fulfilment: FulfilmentVisual,
  customers: CustomersVisual,
  staff: StaffVisual,
  branches: BranchesVisual,
  accounting: AccountingVisual,
  analytics: AnalyticsVisual,
  ai: AiVisual,
};
